<template>
    <div id="reports-list">
        <h2 class="title">{{$t('automatic.title')}}</h2>
        <Message :message="message" :type="type" v-if="message !== ''"></Message>
        <div class="content" v-if="loaded">
            <div class="controls">
                <div class="buttons" v-if="loaded">
                    <div class="adds">
                        <div class="add-link">
                            <font-awesome-icon icon="plus-circle" size="lg"></font-awesome-icon>
                            <button @click.prevent="addReport" href="/">{{$t('automatic.add')}}</button>
                        </div>
                    </div>
                    <div class="ops">
                        <SubmitButton :class="{'validation-error': errors.items.length >0}"
                                      @save="save"></SubmitButton>
                        <CancelButton @cancel="cancel"></CancelButton>
                    </div>
                </div>
            </div>
            <div class="reports">
                <Report :devices="devices" :key="report.id" :report="report" @editSwitch="moveToNew"
                        @removeReport="deleteReport"
                        v-for="report in reports">
                </Report>
            </div>

        </div>
        <Preloader v-else></Preloader>
    </div>
</template>

<script>
import Report from '@/components/reportManager/Report.vue';
import SubmitButton from '@/components/helpers/SubmitButton.vue';
import CancelButton from '@/components/helpers/CancelButton.vue';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import Message from '../../components/helpers/Message';
import Preloader from '../../components/helpers/Preloader';
import { GroupHasReportsError } from '../../api/user/erros';
import AutomaticReportDTO from '@/DTOs/AutomaticReportDTO';

const DataRepository = RepositoryFactory.get('data');
const DeviceRepository = RepositoryFactory.get('devices');
export default {
  name: 'reportsList',
  components: {
    Preloader,
    Message,
    Report,
    SubmitButton,
    CancelButton,
  },
  async mounted() {
    this.$validator.localize(this.$i18n.locale, {
      messages: {
        required: field => this.$t('configuration.validation.singular'),
        min_value: field => this.$t('configuration.validation.negative'),
        email: field => this.$t('automatic.emailValidation'),
      },
    });
    await this.getReports();
    await this.getDevices();
    this.loaded = true;
  },
  watch: {
    async groupId() {
      this.loaded = false;
      await this.getDevices();
      this.loaded = true;
    },
  },
  data() {
    return {
      existingReports: [],
      newReports: [],
      reportsForDeletion: [],
      type: 'warning',
      message: '',
      dummyId: -10,
      loaded: false,
      devices: [],
    };
  },
  computed: {
    reports() {
      return this.newReports.concat(this.existingReports);
    },
    groupId() {
      return this.$store.getters.selectedGroup.id;
    },
  },
  methods: {
    moveToNew(key) {
      const object = this.newReports.find(x => x.id === key);
      if (object === undefined) {
        const index = this.existingReports.findIndex(report => report.id === key);
        const report = this.existingReports[index];
        this.existingReports.splice(index, 1);
        this.newReports.push(report);
      }
    },
    addReport() {
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, '0');
      const mm = String(today.getMonth() + 1).padStart(2, '0'); // January is 0!
      const yyyy = today.getFullYear();
      const hh = today.getHours();
      const MM = today.getMinutes();
      const ss = today.getSeconds();
      const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
      this.newReports.unshift({
        id: this.dummyId,
        name: this.$t('automatic.newReport'),
        delivery: [],
        cron: '',
        reportType: 'HOURLY',
        startDate: `${yyyy}-${mm}-${dd} ${this.formatHour(MM, hh, ss)}`,
        dayInterval: 0,
        monthInterval: 0,
        hourInterval: 0,
        minuteInterval: 0,
        daysNumber: 1,
        nextDate: `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1)
          .padStart(2, '0')}-${String(tomorrow.getDate())
          .padStart(2, '0')}`,
      });
      this.dummyId -= 1;
    },
    formatHour(minutes, hours, seconds) {
      if (minutes < 10) {
        minutes = `0${minutes}`;
      }
      if (hours < 10) {
        hours = `0${hours}`;
      }
      if (seconds < 10) {
        seconds = `0${seconds}`;
      }

      return `${hours}:${minutes}:${seconds}`;
    },
    async deleteReports() {
      for (let index = 0; index < this.reportsForDeletion.length; index += 1) {
        const id = this.reportsForDeletion[index];
        try {
          await DataRepository.deleteAutomaticReport(this.$store.getters.token, id);
        } catch (error) {
          throw error;
        }
      }
    },
    async save() {
      if (this.errors.items.length <= 0) {
        this.loaded = false;
        if (this.reportsForDeletion.length > 0) {
          try {
            await this.deleteReports();
          } catch (error) {
            this.handleError(error);
            return false;
          }
        }
        const payload = {
          reports: [],
        };
        if (this.newReports.length > 0) {
          this.newReports.forEach((report) => {
            payload.reports.push(new AutomaticReportDTO(report, this.devices[0].serialNumber));
          });
        }
        if (payload.reports.length > 0) {
          try {
            await DataRepository.saveReports(this.$store.getters.token, payload);
          } catch (error) {
            this.handleError(error);
            return false;
          }
        }
        await this.saveSuccesful();
        this.loaded = true;
      }
      return true;
    },
    async saveSuccesful() {
      this.message = this.$t('automatic.messages.success');
      this.type = 'info';
      this.newReports = [];
      this.existingReports = [];
      this.reportsForDeletion = [];
      await this.getReports();
    },
    handleError(error) {
      if (error instanceof GroupHasReportsError) {
        this.message = this.$t('messages.intervalError');
      } else {
        this.message = this.$t('automatic.messages.error');
      }
      this.type = 'error';
      this.loaded = true;
    },
    cancel() {
      this.newReports = [];
      this.existingReports = [];
      this.reportsForDeletion = [];
      this.getReports();
      this.message = '';
      this.loaded = true;
    },
    getReports() {
      this.loaded = false;
      DataRepository.getAutomaticReports(this.$store.getters.token).then(
        (response) => {
          this.existingReports = response.reports;
        },
      );
    },
    async getDevices() {
      // this.$store.getters.selectedGroup.id
      this.devices = await DeviceRepository.getDevicesWithShops(this.$store.getters.token, 'all');
    },
    deleteReport(key) {
      if (key < 0) {
        const index = this.newReports.findIndex(report => report.id === key);
        this.newReports.splice(index, 1);
      } else {
        let index = this.existingReports.findIndex(report => report.id === key);
        if (index < 0) {
          index = this.newReports.findIndex(report => report.id === key);
          this.newReports.splice(index, 1);
          this.reportsForDeletion.push(key);
        } else {
          this.existingReports.splice(index, 1);
          this.reportsForDeletion.push(key);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
    .reports {
        display: flex;
        flex-wrap: wrap;
        @include media('<776px') {
            flex-direction: column;
        }
    }

    .controls {
        margin-bottom: 35px;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        @include media('<1450px') {
            flex-wrap: wrap;
        }

        .buttons {
            display: flex;
            @include media('<1450px') {
                width: 100%;
                justify-content: space-between;
            }
            @include media('<470px') {
                flex-direction: column;
            }

            .adds, .ops {
                display: flex;
                justify-content: space-between;
                flex-direction: row;
                @include media('<1200px') {
                    flex-direction: column;
                }
                @include media('<1025px') {
                    flex-direction: row;
                    width: 100%;
                }
                @include media('<776px') {
                    flex-direction: column;

                }

            }

            .adds {
                @include media('<1200px') {
                    align-items: baseline;
                    justify-content: center;
                }
                @include media('<776px') {
                    align-items: baseline;
                    justify-content: center;
                }
            }

            .ops {
                button {
                    @include media('<1200px') {
                        margin: 5px 0;
                    }
                }
            }

        }

        .search-container {
            width: 611px;
            margin-left: 20px;
            @include media('<1450px') {
                width: 100%;
                margin-top: 15px;
                margin-left: 0;
            }
        }

        input {
            border: 1px solid #e5e5e5;
            width: 100%;
            box-sizing: border-box;
            padding: 20px 40px;
            border-radius: 5px;
            margin-left: auto;

        }

        button {
            border: none;
            background: none;
            cursor: pointer;
            color: #005fab;
            font-size: rem(18px);
            font-weight: bold;

            &.cancel, &.save {

                color: white;
                font-size: rem(18px);
                font-weight: bold;
                padding: 12px 50px;
                border-radius: 30px;
                transition: 300ms all ease-in;

            }

            &.save {
                background: #005fab;
                border: 1px solid #005fab;
                margin-left: 60px;
                margin-right: 21px;

                &:hover {
                    color: #005fab;
                    background: white;
                }
            }

            &.cancel {
                background-color: #002678;
                border: 1px solid #002678;

                &:hover {
                    color: #002678;
                    background: white;
                }
            }
        }

        .add-link {
            font-size: rem(18px);
            color: #005fab;
            font-weight: 900;
            line-height: 40px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-right: 36px;
            text-align: left;

            a {
                margin-left: 17px;
                text-decoration: none;

                &:active, &:hover, &:visited {
                    color: #005fab;
                }
            }

        }
    }

    .add-link {
        font-size: rem(18px);
        color: #005fab;
        font-weight: 900;
        line-height: 40px;
        display: flex;
        /*justify-content: space-between;*/
        align-items: center;
        margin-right: 36px;
        text-align: left;

        button {
            margin-left: 17px;
            text-decoration: none;
            border: none;
            color: #005fab;
            background: none;
            font-weight: bold;

            &:active, &:hover, &:visited {
                color: #005fab;
            }
        }

    }

    .button-container {
        padding: 0 15px;
        @include media('<776px') {
            padding: 15px;
        }
    }

    .report-container {
        @include media('<776px') {
            width: 90%;
        }
    }
</style>
